import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Printer, FileText, ArrowLeft, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const blankItem = () => ({ plu_code: "", name: "", quantity: 1, unit_price: 0, line_total: 0 });

const blankForm = () => ({
  invoice_number: "",
  original_date: "",
  salesperson_name: "",
  customer_id: null,
  customer_name: "",
  customer_phone: "",
  customer_address: "",
  customer_vat_number: "",
  customer_id_label: "",
  items: [blankItem()],
  cash: 0,
  eft: 0,
  card: 0,
  other1: 0,
  voucher: 0,
  change: 0,
  reference: "",
  remarks: "",
  source_system: "WIMCO-POS1",
});

const HistoricalInvoices = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blankForm());
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [custQuery, setCustQuery] = useState("");
  const [custDropdownOpen, setCustDropdownOpen] = useState(false);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/historical-invoices`, { params: { q: search } });
      setRows(r.data?.items || []);
    } catch (e) {
      toast.error("Failed to load historical invoices");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const r = await axios.get(`${API}/customers`, { params: { limit: 3000 } });
      const list = Array.isArray(r.data) ? r.data : (r.data?.customers || r.data?.items || []);
      setCustomers(list);
    } catch (e) {
      // Non-fatal — user can still type a free-text name
    }
  };

  useEffect(() => { fetchRows(); fetchCustomers(); /* eslint-disable-next-line */ }, []);

  const matchedCustomers = useMemo(() => {
    const q = (custQuery || "").trim().toLowerCase();
    if (!q) return customers.slice(0, 30);
    return customers
      .filter((c) => (c.name || "").toLowerCase().includes(q) || (c.account_number || "").toLowerCase().includes(q))
      .slice(0, 30);
  }, [customers, custQuery]);

  const buildAddress = (c) => {
    if (!c) return "";
    // Mirror backend's _format_customer_full_address: street_address (required)
    // + suburb + "city, province" + postal_code, deduplicated.
    const street = (c.street_address || "").trim();
    if (!street) return "";
    const lines = [street];
    const append = (v) => {
      const s = (v || "").trim();
      if (s && !lines.includes(s)) lines.push(s);
    };
    append(c.suburb);
    const cityProv = [c.city, c.province].filter(Boolean).join(", ");
    append(cityProv);
    append(c.postal_code);
    return lines.join("\n");
  };

  const pickCustomer = (c) => {
    setForm((f) => ({
      ...f,
      customer_id: c.id,
      customer_name: c.name || "",
      customer_phone: c.phone || "",
      customer_address: buildAddress(c),
      customer_vat_number: c.vat_number || "",
    }));
    setCustQuery(c.name || "");
    setCustDropdownOpen(false);
  };

  const clearCustomerLink = () => {
    setForm((f) => ({ ...f, customer_id: null }));
    toast.info("Customer link cleared. Edit the fields manually below.");
  };

  const computedTotal = useMemo(() => {
    return (form.items || []).reduce((sum, it) => sum + Number(it.line_total || 0), 0);
  }, [form.items]);

  const updateItem = (idx, patch) => {
    setForm((f) => {
      const items = [...f.items];
      items[idx] = { ...items[idx], ...patch };
      // auto-calc line_total when qty / unit_price changes
      const q = Number(items[idx].quantity || 0);
      const p = Number(items[idx].unit_price || 0);
      if (patch.quantity !== undefined || patch.unit_price !== undefined) {
        items[idx].line_total = Number((q * p).toFixed(2));
      }
      return { ...f, items };
    });
  };

  const addItemRow = () => setForm((f) => ({ ...f, items: [...f.items, blankItem()] }));
  const removeItemRow = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const submit = async () => {
    if (!form.invoice_number.trim()) return toast.error("Invoice number is required");
    if (!form.original_date.trim()) return toast.error("Original date is required");
    if (!form.customer_name.trim()) return toast.error("Customer name is required");
    if (!form.items.length || !form.items.some((it) => it.name)) return toast.error("Add at least one line item");
    setSaving(true);
    try {
      const payload = {
        ...form,
        total: Number(computedTotal.toFixed(2)),
        items: form.items.filter((it) => it.name).map((it) => ({
          plu_code: it.plu_code || "",
          name: it.name,
          quantity: Number(it.quantity || 0),
          unit_price: Number(it.unit_price || 0),
          line_total: Number(it.line_total || 0),
        })),
      };
      const r = await axios.post(`${API}/historical-invoices`, payload);
      toast.success(`Historical invoice ${r.data.invoice_number_display} saved`);
      setOpen(false);
      setForm(blankForm());
      setCustQuery("");
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, invNo) => {
    if (!window.confirm(`Delete historical invoice ${invNo}?`)) return;
    try {
      await axios.delete(`${API}/historical-invoices/${id}`);
      toast.success("Deleted");
      fetchRows();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handlePrint = (invNo) => {
    // Hand off to existing SARS reprint flow — Document Lookup will find this
    // historical invoice via the shared `/documents/sars-reprint/{number}`
    // endpoint and render the standard PDF.
    window.location.href = `/document-lookup?sars=${encodeURIComponent(invNo)}`;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="historical-invoices-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => window.location.href = "/document-lookup"} data-testid="back-to-doc-lookup">
            <ArrowLeft className="w-4 h-4 mr-1" /> Document Lookup
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><History className="w-6 h-6" /> Historical Invoices</h1>
            <p className="text-sm text-gray-600">
              Re-entered invoices from your previous POS (e.g. WIMCO-POS1). These print through the SARS template
              with the original date & invoice number. They do <strong>not</strong> affect stock, customer balances,
              or any financial report — purely for SARS reprint.
            </p>
          </div>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-amber-700 hover:bg-amber-800 text-white" data-testid="add-historical-invoice-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Historical Invoice
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Saved Historical Invoices</CardTitle>
          <CardDescription>{rows.length} invoice{rows.length === 1 ? "" : "s"} on file</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-3">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") fetchRows(); }}
              placeholder="Search by invoice # or customer name…"
              className="flex-1"
              data-testid="historical-search-input"
            />
            <Button variant="outline" onClick={fetchRows} data-testid="historical-search-btn">Search</Button>
          </div>

          <div className="border rounded overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-28">Invoice #</TableHead>
                  <TableHead className="w-40">Original Date</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead className="w-28">Salesperson</TableHead>
                  <TableHead className="w-20 text-center">Items</TableHead>
                  <TableHead className="w-32 text-right">Total</TableHead>
                  <TableHead className="w-32"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-6 text-gray-500">Loading…</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    No historical invoices yet. Click <strong>Add Historical Invoice</strong> to enter your first one.
                  </TableCell></TableRow>
                ) : rows.map((r) => (
                  <TableRow key={r.id} data-testid={`historical-row-${r.invoice_number_display}`}>
                    <TableCell className="font-mono font-semibold">{r.invoice_number_display}</TableCell>
                    <TableCell className="text-sm">{r.original_date ? new Date(r.original_date).toLocaleString("en-ZA", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</TableCell>
                    <TableCell>{r.customer_name}</TableCell>
                    <TableCell className="text-sm">{r.salesperson_name || "—"}</TableCell>
                    <TableCell className="text-center">{(r.items || []).length}</TableCell>
                    <TableCell className="text-right font-mono">R {Number(r.total || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => handlePrint(r.invoice_number_display)} data-testid={`print-historical-${r.invoice_number_display}`}>
                        <Printer className="w-3.5 h-3.5 mr-1" /> SARS
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(r.id, r.invoice_number_display)} className="text-red-600 hover:text-red-700 ml-1" data-testid={`delete-historical-${r.invoice_number_display}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Entry form */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="historical-invoice-form">
          <DialogHeader>
            <DialogTitle>Add Historical Invoice</DialogTitle>
            <DialogDescription>
              Enter the original invoice exactly as it appeared in your old system. Everything is preserved as-is.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Top row — invoice number + date + salesperson */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Invoice Number *</Label>
                <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} placeholder="e.g. 21149" data-testid="form-invoice-number" />
              </div>
              <div>
                <Label>Original Date *</Label>
                <Input value={form.original_date} onChange={(e) => setForm({ ...form, original_date: e.target.value })} placeholder="05-Jan-2026: 09:47:13" data-testid="form-original-date" />
                <p className="text-[10px] text-gray-500 mt-1">Format: DD-Mon-YYYY: HH:MM:SS (as shown on the old invoice)</p>
              </div>
              <div>
                <Label>Sales Person</Label>
                <Input value={form.salesperson_name} onChange={(e) => setForm({ ...form, salesperson_name: e.target.value })} placeholder="e.g. ESTER" data-testid="form-salesperson" />
              </div>
            </div>

            {/* Customer block */}
            <div className="border rounded p-3 bg-gray-50/50 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-gray-700">Customer</div>
                {form.customer_id && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium flex items-center gap-1.5">
                    Live-linked — current Reg# / VAT / address will print
                    <button type="button" onClick={clearCustomerLink} className="text-emerald-700 hover:text-red-600 underline ml-1" data-testid="clear-customer-link">unlink</button>
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2 relative">
                  <Label>Name * <span className="text-gray-400 font-normal">(pick from list or type a new name)</span></Label>
                  <Input
                    value={form.customer_id ? form.customer_name : custQuery}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCustQuery(v);
                      setCustDropdownOpen(true);
                      // Typing breaks any existing link — user is editing manually
                      setForm((f) => ({ ...f, customer_id: null, customer_name: v }));
                    }}
                    onFocus={() => setCustDropdownOpen(true)}
                    onBlur={() => setTimeout(() => setCustDropdownOpen(false), 180)}
                    placeholder="Start typing a customer name…"
                    autoComplete="off"
                    data-testid="form-customer-name"
                  />
                  {custDropdownOpen && matchedCustomers.length > 0 && (
                    <div className="absolute z-30 left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-white border rounded shadow-lg" data-testid="customer-dropdown">
                      {matchedCustomers.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => pickCustomer(c)}
                          className="w-full text-left px-3 py-2 hover:bg-amber-50 border-b last:border-0 text-sm"
                          data-testid={`customer-option-${c.id}`}
                        >
                          <div className="font-medium">{c.name}</div>
                          <div className="text-xs text-gray-500">
                            {c.account_number ? `Acc: ${c.account_number}` : ""}
                            {c.company_registration_number ? `  ·  Reg: ${c.company_registration_number}` : ""}
                            {c.vat_number ? `  ·  VAT: ${c.vat_number}` : ""}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} disabled={!!form.customer_id} placeholder="0725937779" data-testid="form-customer-phone" />
                </div>
                <div>
                  <Label>VAT Reg #</Label>
                  <Input value={form.customer_vat_number} onChange={(e) => setForm({ ...form, customer_vat_number: e.target.value })} disabled={!!form.customer_id} placeholder="4320256995" data-testid="form-customer-vat" />
                </div>
                <div className="md:col-span-2">
                  <Label>Address (one line per row)</Label>
                  <Textarea rows={2} value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} disabled={!!form.customer_id} placeholder="PLAAS MELKBOSCH" data-testid="form-customer-address" />
                </div>
                <div>
                  <Label>Cust ID (old system)</Label>
                  <Input value={form.customer_id_label} onChange={(e) => setForm({ ...form, customer_id_label: e.target.value })} placeholder="DAW566211B" data-testid="form-customer-id" />
                </div>
              </div>
              {form.customer_id && (
                <p className="text-[11px] text-emerald-700 leading-snug">
                  📌 This invoice is linked to a customer in your live database. When you print the SARS reprint, the system pulls the customer&apos;s <strong>current</strong> name, address, phone, VAT # and registration # — so any updates you make to the customer record later will automatically reflect on this historical invoice.
                </p>
              )}
            </div>

            {/* Line items */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-gray-700">Line Items</div>
                <Button type="button" size="sm" variant="outline" onClick={addItemRow} data-testid="add-line-item">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Line
                </Button>
              </div>
              <div className="border rounded overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-24">Code</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-20 text-right">Qty</TableHead>
                      <TableHead className="w-28 text-right">Unit Price</TableHead>
                      <TableHead className="w-28 text-right">Total</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {form.items.map((it, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Input value={it.plu_code} onChange={(e) => updateItem(idx, { plu_code: e.target.value })} placeholder="171009" className="h-8 text-sm" data-testid={`item-code-${idx}`} />
                        </TableCell>
                        <TableCell>
                          <Input value={it.name} onChange={(e) => updateItem(idx, { name: e.target.value })} placeholder="MAGN SUPER MAIZE MEAL 12.5KG" className="h-8 text-sm" data-testid={`item-name-${idx}`} />
                        </TableCell>
                        <TableCell>
                          <Input type="number" value={it.quantity} onChange={(e) => updateItem(idx, { quantity: e.target.value })} className="h-8 text-sm text-right" data-testid={`item-qty-${idx}`} />
                        </TableCell>
                        <TableCell>
                          <Input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(idx, { unit_price: e.target.value })} className="h-8 text-sm text-right" data-testid={`item-price-${idx}`} />
                        </TableCell>
                        <TableCell>
                          <Input type="number" step="0.01" value={it.line_total} onChange={(e) => updateItem(idx, { line_total: e.target.value })} className="h-8 text-sm text-right" data-testid={`item-total-${idx}`} />
                        </TableCell>
                        <TableCell>
                          {form.items.length > 1 && (
                            <Button type="button" size="sm" variant="ghost" onClick={() => removeItemRow(idx)} className="text-red-600 h-8 px-1" data-testid={`remove-item-${idx}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Payment split + totals */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="border rounded p-3 space-y-2 bg-gray-50/50">
                <div className="text-sm font-semibold text-gray-700">Payment Split (optional)</div>
                <div className="grid grid-cols-2 gap-2">
                  {["cash", "card", "eft", "other1", "voucher"].map((k) => (
                    <div key={k}>
                      <Label className="capitalize text-xs">{k}</Label>
                      <Input type="number" step="0.01" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="h-8 text-sm" data-testid={`form-pay-${k}`} />
                    </div>
                  ))}
                  <div>
                    <Label className="text-xs">Change</Label>
                    <Input type="number" step="0.01" value={form.change} onChange={(e) => setForm({ ...form, change: e.target.value })} className="h-8 text-sm" data-testid="form-change" />
                  </div>
                </div>
              </div>
              <div className="border rounded p-3 space-y-2 bg-amber-50/40">
                <div className="text-sm font-semibold text-gray-700">Totals & Notes</div>
                <div className="flex justify-between text-base font-bold">
                  <span>Total this invoice:</span>
                  <span className="font-mono" data-testid="form-total">R {computedTotal.toFixed(2)}</span>
                </div>
                <div>
                  <Label>Reference</Label>
                  <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="h-8 text-sm" data-testid="form-reference" />
                </div>
                <div>
                  <Label>Remarks</Label>
                  <Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="h-8 text-sm" data-testid="form-remarks" />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="form-cancel">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-amber-700 hover:bg-amber-800 text-white" data-testid="form-save">
              {saving ? "Saving…" : "Save Historical Invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HistoricalInvoices;
